import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaBriefcase, FaGraduationCap } from 'react-icons/fa';

const experiences = [
  {
    type: 'work',
    title: 'Full-Stack Developer',
    company: 'Tech Solutions Inc.',
    location: 'Remote',
    period: '2024 - Present',
    description: [
      'Developing and maintaining scalable web applications using MERN stack',
      'Implementing RESTful APIs and microservices architecture',
      'Collaborating with cross-functional teams using Agile methodologies',
      'Optimized application performance resulting in 40% faster load times',
    ],
    technologies: ['React', 'Node.js', 'MongoDB', 'AWS'],
  },
  {
    type: 'work',
    title: 'Frontend Developer Intern',
    company: 'Digital Innovations',
    location: 'Bangalore, India',
    period: '2023 - 2024',
    description: [
      'Built responsive web interfaces using React and TailwindCSS',
      'Integrated third-party APIs and payment gateways',
      'Participated in code reviews and followed best practices',
      'Developed reusable component library for company projects',
    ],
    technologies: ['React', 'JavaScript', 'Tailwind', 'Git'],
  },
  {
    type: 'work',
    title: 'Freelance Web Developer',
    company: 'Self-Employed',
    location: 'Remote',
    period: '2022 - 2023',
    description: [
      'Created custom websites for small businesses and startups',
      'Provided end-to-end solutions from design to deployment',
      'Managed client relationships and project timelines',
      'Delivered 15+ successful projects with 100% client satisfaction',
    ],
    technologies: ['HTML/CSS', 'JavaScript', 'WordPress', 'PHP'],
  },
  {
    type: 'education',
    title: 'B.Tech in Computer Science',
    company: 'Government College Dharamshala',
    location: 'Dharamshala, HP',
    period: '2020 - 2024',
    description: [
      'Graduated with CGPA: 7.6/10',
      'Specialized in Web Development and Software Engineering',
      'Led technical workshops and coding bootcamps',
      'Active member of the College Coding Club',
    ],
    technologies: ['DSA', 'DBMS', 'System Design', 'Web Technologies'],
  },
];

const ExperienceCard = ({ experience, index, isLeft }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const Icon = experience.type === 'work' ? FaBriefcase : FaGraduationCap;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: isLeft ? -50 : 50 }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className={`flex items-start gap-4 ${isLeft ? 'md:flex-row-reverse md:text-right' : ''}`}
    >
      {/* Content */}
      <div className="flex-1 bg-gray-800/80 border border-green-900 rounded-lg p-6 hover:border-bb-green transition-all group">
        <motion.div
          whileHover={{ scale: 1.02 }}
          className="relative"
        >
          {/* Period Badge */}
          <span className="inline-block px-3 py-1 bg-bb-green/20 border border-bb-green rounded-full text-bb-green-light text-sm mb-3">
            {experience.period}
          </span>

          <h3 className="text-xl font-bold text-white mb-1">{experience.title}</h3>
          <p className="text-bb-green-light font-semibold mb-1">{experience.company}</p>
          <p className="text-gray-500 text-sm mb-4">{experience.location}</p>

          <ul className={`space-y-2 mb-4 ${isLeft ? 'md:text-right' : ''}`}>
            {experience.description.map((item, i) => (
              <li key={i} className="text-gray-400 text-sm flex items-start gap-2">
                <span className={`text-bb-green mt-1 ${isLeft ? 'md:order-2' : ''}`}>▸</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>

          {/* Technologies */}
          <div className={`flex flex-wrap gap-2 ${isLeft ? 'md:justify-end' : ''}`}>
            {experience.technologies.map((tech) => (
              <span
                key={tech}
                className="px-2 py-1 bg-gray-900 border border-green-800 rounded text-xs text-gray-300"
              >
                {tech}
              </span>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Timeline Icon - Hidden on mobile */}
      <div className="hidden md:flex flex-col items-center">
        <motion.div
          className="w-12 h-12 bg-gray-900 border-2 border-bb-green rounded-full flex items-center justify-center z-10"
          whileHover={{
            scale: 1.2,
            boxShadow: "0 0 20px rgba(0, 200, 83, 0.6)"
          }}
        >
          <Icon className="text-bb-green-light" />
        </motion.div>
      </div>

      {/* Empty space for alignment - Hidden on mobile */}
      <div className="hidden md:block flex-1" />
    </motion.div>
  );
};

const Experience = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <section id="experience" className="py-20 px-4 relative">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 right-10 text-8xl font-bold text-green-500 transform rotate-12">Br</div>
        <div className="absolute bottom-20 left-10 text-8xl font-bold text-green-500 transform -rotate-12">Ba</div>
      </div>

      <div ref={ref} className="max-w-4xl mx-auto relative z-10">
        {/* Section Title */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="text-white">The </span>
            <span className="text-bb-green-light">Timeline</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            My journey from a curious student to a code-cooking professional.
            Every experience has added to my formula for success.
          </p>
          <div className="w-24 h-1 bg-bb-green mx-auto mt-4" />
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Timeline Line - Hidden on mobile */}
          <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-gradient-to-b from-bb-green via-green-600 to-green-900" />

          {/* Experience Items */}
          <div className="space-y-8 md:space-y-12">
            {experiences.map((exp, index) => (
              <ExperienceCard
                key={index}
                experience={exp}
                index={index}
                isLeft={index % 2 === 0}
              />
            ))}
          </div>
        </div>

        {/* End of Timeline */}
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={inView ? { opacity: 1, scale: 1 } : {}}
          transition={{ delay: 0.5 }}
          className="hidden md:flex justify-center mt-8"
        >
          <div className="w-16 h-16 bg-bb-green rounded-full flex items-center justify-center shadow-lg shadow-green-900/50">
            <span className="text-white font-bold text-sm text-center">TO BE<br/>CONT.</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Experience;
